// @ts-nocheck

import * as React from 'react';
import type { ComponentType } from 'react';
import styled from 'styled-components';

import ScaleTransitionGroup from '../../animations/ScaleTransitionGroup';
import Overlay from '../overlay';

const logoW112 = require('../../../../src/assets/img/mobile/logo_winamax_112x112.png');
const logoW224 = require('../../../../src/assets/img/mobile/logo_winamax_224x224.png');
const logoW56 = require('../../../../src/assets/img/mobile/logo_winamax_56x56.png');

const AlertWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 10001;
`;

const AlertBox = styled.div`
  box-sizing: border-box;
  padding: 0;
  margin: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16em;
  background-color: #f4f4f4;
  border-radius: 2px;
  max-width: 95%;
  color: #1f1f21;
  user-select: none;
`;

const AlertContentWrapper = styled.div`
  padding-top: 1em;
  overflow: hidden;
`;

const Logo = styled.div`
  width: 4em;
  height: 4em;
  margin: 0 auto 0.5em auto;
  background-image: url(${logoW56});
  background-repeat: no-repeat;
  background-size: 100% 100%;

  /* stylelint-disable  */
  @media screen and (-webkit-min-device-pixel-ratio: 2),
    screen and (min--moz-device-pixel-ratio: 2),
    screen and (-o-min-device-pixel-ratio: 2/1),
    screen and (min-device-pixel-ratio: 2),
    screen and (min-resolution: 2dppx) {
    background-image: url(${logoW112});
  }
  @media screen and (-webkit-min-device-pixel-ratio: 3),
    screen and (min--moz-device-pixel-ratio: 3),
    screen and (-o-min-device-pixel-ratio: 3/1),
    screen and (min-device-pixel-ratio: 3),
    screen and (min-resolution: 3dppx) {
    background-image: url(${logoW224});
  }
  /* stylelint-enable */
`;

const AlertTitle = styled.div`
  font-weight: bold;
  padding: 0 1em;
  text-align: center;
  color: #333;
`;

const AlertContent = styled.div`
  box-sizing: border-box;
  background-clip: padding-box;
  padding: 0.25em 1em 1.5em 1em;
  font-size: 0.8em;
  min-height: 3em;
  text-align: center;
  color: #333;
`;

const AlertFooter = styled.div`
  border-top: 1px solid #ddd;
`;

type AlertButtonProps = { width: string; isColumn?: boolean | null; isDefault?: boolean | null };

const AlertButton: ComponentType<AlertButtonProps> = styled.button`
  &&& {
    display: inline-block;
    width: ${({ width }) => width || '100%'};
    height: 2.75em;
    padding: 0 0.5em;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    vertical-align: middle;
    text-align: center;
    line-height: 2.75em;
    outline: none;
    color: #666;

    :not(:last-child) {
      ${({ isColumn }) => (isColumn ? 'border-bottom' : 'border-right')}: 1px solid #ddd;
    }

    :active {
      background-color: #e8e8e8;
    }

    ${({ isDefault }) => {
      if (isDefault) {
        return `
        color: #f01a26;
        font-weight: bold;
      `;
      }
    }}
  }
`;

/*
 * Alert Dialog component with Buttons at the bottom.
 *
 * title: title of the alert.
 * content: description of the alert.
 * isColumn: display the buttons in columns, default in row.
 * buttons: array describing the buttons of the alert
 *    * label: label of the button
 *    * handler: function to be called on click
 *    * isDefault: preferred choice, special style applied
 *
 * isOpen: wether to display or not the Dialog.
 * isCancelable: wether the Dialog is dismissible (click outside the Dialog box), need an onCancel function.
 * onCancel: function called on a click outside the Dialog box, if isCancelable is true.
 */

type Button = { label: string; handler: () => void; isDefault?: boolean | null };

type Props = {
  title?: string | null;
  content?: string | null;
  isColumn?: boolean | null;
  buttons?: Array<Button>;
  //dialogProps
  isOpen: boolean | null;
  isCancelable?: boolean | null;
  onCancel?: () => void;
};

const AlertDialog = (props: Props) => {
  const { isOpen, isCancelable, onCancel, title, content, isColumn, buttons } = props;
  const elementRef = React.useRef<React.ElementRef<'div'> | null>(null);
  const saveHandlerRef = React.useRef(onCancel);

  const handleOutsideClick = React.useCallback(
    (event: Event) => {
      if (isOpen && isCancelable && typeof saveHandlerRef.current === 'function') {
        if (elementRef.current && event.target instanceof Node && !elementRef.current.contains(event.target)) {
          saveHandlerRef.current();
        }
      }
    },
    [isOpen, isCancelable],
  );

  React.useEffect(() => {
    saveHandlerRef.current = onCancel;
  }, [onCancel]);

  React.useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
    };
  }, [handleOutsideClick]);

  return (
    <React.Fragment>
      <ScaleTransitionGroup>
        {isOpen && (
          <AlertWrapper>
            <AlertBox ref={elementRef}>
              <AlertContentWrapper>
                <Logo />
                {title && <AlertTitle>{title}</AlertTitle>}
                {content && <AlertContent>{content}</AlertContent>}
                {buttons && (
                  <AlertFooter>
                    {buttons.map(({ label, handler, isDefault }, index) => (
                      <AlertButton
                        key={index}
                        onClick={handler}
                        isColumn={isColumn}
                        width={isColumn ? '100%' : 100 / buttons.length + '%'}
                        isDefault={isDefault}
                      >
                        {label}
                      </AlertButton>
                    ))}
                  </AlertFooter>
                )}
              </AlertContentWrapper>
            </AlertBox>
          </AlertWrapper>
        )}
      </ScaleTransitionGroup>
      <Overlay isOpen={isOpen} />
    </React.Fragment>
  );
};

export default AlertDialog;
